import { Plus } from 'lucide-react';
import { TeamsTab } from './TeamsTab';
import { EmployeeOnboardingWizard } from './EmployeeOnboardingWizard';
import { usePeopleDirectory } from './usePeopleDirectory';
import { PeopleDirectoryList } from './PeopleDirectoryList';
import { PeopleLegacyForms } from './PeopleLegacyForms';
import { useCanWritePeople } from './peopleWriteAccess';

export function EmployeesPage() {
  const state = usePeopleDirectory();
  const canWrite = useCanWritePeople();
  const { tab, setTab, showWizard, setShowWizard, editingEmployeeId, setEditingEmployeeId, fetchEmployees } = state;
  return (
    <div className="aura-root">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1
            style={{
              fontFamily: 'var(--ff-serif)',
              fontSize: 28,
              color: 'var(--ink-0)',
              margin: 0,
            }}
          >
            People
          </h1>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 2 }}>
            {tab === 'teams'
              ? 'People and teams'
              : 'Manage your workforce'}
          </p>
        </div>
        {tab === 'employees' && canWrite && (
          <button
            onClick={() => setShowWizard(true)}
            className="btn btn-primary max-sm:min-h-[40px]"
          >
            <Plus size={14} />
            Add Employee
          </button>
        )}
      </div>

      {/* Tab strip */}
      <div
        className="row center"
        style={{
          gap: 4,
          marginBottom: 20,
          borderBottom: '1px solid var(--line)',
        }}
      >
        <TabButton
          label="People"
          active={tab === 'employees'}
          onClick={() => setTab('employees')}
        />
        <TabButton
          label="Teams"
          active={tab === 'teams'}
          onClick={() => setTab('teams')}
        />
      </div>

      {tab === 'teams' ? <TeamsTab /> : (
      <>
        <PeopleDirectoryList state={state} />
      </>
      )}

      {/* LBR2-C3 — Onboarding wizard (linked User+Employee + KYC + vehicle). */}
      <EmployeeOnboardingWizard
        open={showWizard}
        onClose={() => setShowWizard(false)}
        onCreated={fetchEmployees}
      />

      {/* Same component, edit mode. Keyed by id so switching rows remounts it
          and cannot show the previous employee's values for a frame. */}
      <EmployeeOnboardingWizard
        key={editingEmployeeId ?? 'no-edit'}
        open={!!editingEmployeeId}
        employeeId={editingEmployeeId}
        onClose={() => setEditingEmployeeId(null)}
        onCreated={fetchEmployees}
      />

      <PeopleLegacyForms state={state} />
    </div>
  );
}

// ── Tab strip button (warm-ivory, saffron for active) ───────────────────────
function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className="max-sm:min-h-[40px]"
      style={{
        border: 'none',
        background: active ? 'var(--saffron-50)' : 'transparent',
        color: active ? 'var(--saffron-600)' : 'var(--ink-2)',
        fontFamily: 'var(--ff-sans)',
        fontSize: 13,
        fontWeight: 500,
        padding: '8px 16px',
        borderTopLeftRadius: 'var(--r-sm)',
        borderTopRightRadius: 'var(--r-sm)',
        borderBottom: active
          ? '2px solid var(--saffron)'
          : '2px solid transparent',
        marginBottom: -1,
        cursor: 'pointer',
        transition: 'background 0.12s, color 0.12s, border-color 0.12s',
      }}
      onMouseEnter={(e) => {
        if (!active)
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-0)';
      }}
      onMouseLeave={(e) => {
        if (!active)
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--ink-2)';
      }}
    >
      {label}
    </button>
  );
}
